import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";
import {
  ArrowLeft, Mail, Phone, FileText, Plus, Loader2, Pause, Play,
  XCircle, RefreshCw, PackagePlus, Calendar, DollarSign,
  Sparkles, ChevronRight, Rocket, Workflow, ExternalLink,
} from "lucide-react";
import { StatusPill, fmtRelative } from "@/lib/workflow";
import { devWarn } from "@/lib/devlog";

const CATEGORY_TINT = {
  sleep: "bg-indigo-50 text-indigo-700",
  metabolic: "bg-amber-50 text-amber-800",
  hormone: "bg-rose-50 text-rose-700",
  diagnostics: "bg-blue-50 text-blue-700",
  wellness: "bg-emerald-50 text-emerald-700",
  longevity: "bg-violet-50 text-violet-700",
  recovery: "bg-orange-50 text-orange-700",
};
const STATUS_BADGE = {
  active: "bg-emerald-100 text-emerald-800",
  paused: "bg-amber-100 text-amber-800",
  cancelled: "bg-rose-100 text-rose-800",
  completed: "bg-slate-200 text-slate-700",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};

export default function PortalConsumerProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [consumer, setConsumer] = useState(null);
  const [services, setServices] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [showActivate, setShowActivate] = useState(false);
  const [pickedProgram, setPickedProgram] = useState("");
  const [activateNotes, setActivateNotes] = useState("");
  const [recs, setRecs] = useState(null);
  const [workflowRows, setWorkflowRows] = useState([]);

  const loadWorkflows = async (consumerObj) => {
    if (!consumerObj) return;
    try {
      const r = await api.get("/workflow/requests");
      const rows = r.data || [];
      const em = (consumerObj.email || "").toLowerCase();
      const parts = (consumerObj.name || "").trim().split(/\s+/);
      const firstName = (parts[0] || "").toLowerCase();
      const lastInit = ((parts[parts.length - 1] || "")[0] || "").toLowerCase();
      const matched = rows.filter((it) => {
        const rowEm = (it.consumer_email || "").toLowerCase();
        if (em && rowEm && rowEm === em) return true;
        const fn = (it.consumer_first_name || "").toLowerCase();
        const li = (it.consumer_last_initial || "").toLowerCase();
        return !!firstName && fn === firstName && li === lastInit;
      });
      setWorkflowRows(matched);
    } catch (e) {
      // non-fatal — workflow rows are auxiliary; surface for debug, not user.
      devWarn("[PortalConsumerProfile] loadWorkflowRows failed:", e);
    }
  };

  const loadRecs = async () => {
    try {
      const r = await api.get(`/clinic/recommendations/consumer/${id}`);
      setRecs(r.data);
    } catch (e) {
      // best-effort — recs are non-blocking for the page.
      devWarn("[PortalConsumerProfile] loadRecs failed:", e);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, s, p] = await Promise.all([
        api.get(`/consumers`).then((r) => (r.data || []).find((x) => x.id === id)),
        api.get(`/consumers/${id}/services`).then((r) => r.data),
        api.get(`/clinic/programs`).then((r) => r.data),
      ]);
      if (!c) { toast.error("Consumer not found"); nav("/portal/consumers"); return; }
      setConsumer(c);
      setServices(s || []);
      setPrograms((p || []).filter((x) => x.status === "active"));
      loadWorkflows(c);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); loadRecs(); }, [id]);

  const activate = async () => {
    if (!pickedProgram) { toast.error("Pick a program first"); return; }
    setActivating(true);
    try {
      await api.post(`/consumers/${id}/services`, {
        program_id: pickedProgram,
        notes: activateNotes || null,
      });
      toast.success("Service activated.");
      setShowActivate(false); setPickedProgram(""); setActivateNotes("");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setActivating(false); }
  };

  const setStatus = async (sid, status) => {
    try {
      await api.patch(`/consumer-services/${sid}`, { status });
      toast.success(`Service ${status}.`);
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (sid) => {
    if (!window.confirm("Delete this service permanently? Use Cancel to preserve history.")) return;
    try { await api.delete(`/consumer-services/${sid}`); toast.success("Service removed."); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <PageShell eyebrow="Consumer" title="Loading…" testId="consumer-profile-loading" />;
  if (!consumer) return null;

  const active = services.filter((s) => s.status === "active");
  const past = services.filter((s) => s.status !== "active");

  return (
    <PageShell
      eyebrow="Consumer profile"
      title={consumer.name}
      intro={null}
      testId={`consumer-profile-${consumer.id}`}
      actions={
        <div className="flex items-center gap-2">
          <Link to="/portal/consumers" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to consumers
          </Link>
          <button
            onClick={() => setShowActivate(true)}
            data-testid="activate-service-btn"
            className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
          >
            <PackagePlus className="w-3.5 h-3.5" /> Activate service
          </button>
        </div>
      }
    >
      {/* Consumer summary card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-5 grid sm:grid-cols-2 gap-4">
        <div className="flex items-center gap-2 text-sm text-slate-700">
          <Mail className="w-4 h-4 text-slate-400" /> {consumer.email}
        </div>
        {consumer.phone && (
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <Phone className="w-4 h-4 text-slate-400" /> {consumer.phone}
          </div>
        )}
        <div className="text-xs text-slate-500">
          Source: <b className="capitalize text-slate-800">{consumer.source}</b> · Status:{" "}
          <span className="inline-block rounded-full px-2 py-0.5 text-[10px] uppercase font-bold bg-slate-100 text-slate-700">{consumer.status}</span>
        </div>
        <div className="text-xs text-slate-500">Added {fmtDate(consumer.created_at)}</div>
        {consumer.notes && (
          <div className="sm:col-span-2 text-sm text-slate-700 inline-flex items-start gap-2">
            <FileText className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" /> {consumer.notes}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-2.5 mb-5 text-[11px] text-blue-900">
        <b>Operational only.</b> Activating a service enrolls this consumer in a clinic-defined program. Nodera tracks billing cadence + status — clinical decisions remain entirely with the clinic.
      </div>

      {/* Activate modal */}
      {showActivate && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-5" data-testid="activate-service-form">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-900">Activate a service for {consumer.name}</div>
            <button onClick={() => setShowActivate(false)} className="text-xs text-slate-500 hover:text-slate-900">Cancel</button>
          </div>
          {programs.length === 0 ? (
            <div className="text-sm text-slate-500 py-3">
              No active programs available.{" "}
              <Link to="/portal/programs" className="text-blue-700 underline">Build one in Program Builder</Link>.
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-3">
                {programs.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPickedProgram(p.id)}
                    data-testid={`pick-program-${p.id}`}
                    className={`text-left rounded-xl border p-3 transition ${
                      pickedProgram === p.id
                        ? "border-slate-900 bg-slate-50 ring-2 ring-slate-900/10"
                        : "border-slate-200 bg-white hover:border-slate-400"
                    }`}
                  >
                    <div className={`inline-block rounded-full px-2 py-0.5 text-[10px] uppercase font-bold ${CATEGORY_TINT[p.category] || "bg-slate-100 text-slate-700"}`}>
                      {p.category}
                    </div>
                    <div className="font-semibold text-slate-900 mt-1">{p.name}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 capitalize">
                      {p.cadence?.replace("_", " ")} · ${p.price || 0}
                    </div>
                  </button>
                ))}
              </div>
              <textarea
                rows={2}
                value={activateNotes}
                onChange={(e) => setActivateNotes(e.target.value)}
                placeholder="Operational notes (optional) — billing preferences, contact channel, kickoff date…"
                data-testid="activate-notes"
                className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 mb-3 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
              />
              <button
                onClick={activate}
                disabled={!pickedProgram || activating}
                data-testid="activate-confirm"
                className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60"
              >
                {activating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackagePlus className="w-3.5 h-3.5" />}
                {activating ? "Activating…" : "Activate"}
              </button>
            </>
          )}
        </div>
      )}

      {/* Best-fit program recommendations driven by screener results. */}
      {recs && recs.recommendations.length > 0 && (
        <Section title="Best-fit recommendations" testId="consumer-recs" className="mb-5">
          <div
            className="rounded-2xl border border-blue-200 bg-blue-50/40 p-4"
            data-testid="consumer-recommendations-panel"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-700 inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Driven by screening results
                </div>
                <div className="text-[12px] text-slate-700 mt-1 leading-snug">
                  Top {recs.recommendations.length} program{recs.recommendations.length === 1 ? "" : "s"} matched against{" "}
                  <b>{recs.submissions_count}</b> screening submission
                  {recs.submissions_count === 1 ? "" : "s"}
                  {recs.screener_categories.length > 0 && (
                    <span> across <b>{recs.screener_categories.join(", ")}</b></span>
                  )}.
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {recs.recommendations.map((r) => (
                <div
                  key={r.id}
                  data-testid={`consumer-rec-${r.id}`}
                  className="rounded-xl border border-slate-200 bg-white p-3 flex flex-col"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[9px] uppercase font-bold ${CATEGORY_TINT[r.category] || "bg-slate-100 text-slate-700"}`}
                    >
                      {r.category}
                    </span>
                    <span className="font-semibold text-slate-900 text-sm flex-1 min-w-0 truncate">
                      {r.name}
                    </span>
                    <span
                      className="inline-flex items-center justify-center bg-blue-600 text-white rounded-full text-[10px] font-bold min-w-[2.25rem] h-5 px-1.5"
                      title="Match score · 0–100"
                    >
                      {r.score}
                    </span>
                  </div>
                  <ul className="space-y-0.5 mb-2">
                    {r.reasons.slice(0, 3).map((reason, i) => (
                      <li key={i} className="text-[11px] text-slate-600 flex items-start gap-1">
                        <ChevronRight className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
                        {reason}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto flex items-center justify-between gap-2 pt-2 border-t border-slate-100">
                    <span className="text-[10px] text-slate-500 capitalize">
                      {(r.cadence || "").replace("_", " ")}
                      {r.price != null && (
                        <span className="ml-1 text-slate-700 font-semibold">· ${r.price}</span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setPickedProgram(r.id);
                        setShowActivate(true);
                      }}
                      data-testid={`consumer-rec-activate-${r.id}`}
                      className="inline-flex items-center gap-1 bg-emerald-600 text-white rounded-full px-2.5 py-1 text-[11px] font-semibold hover:bg-emerald-700"
                    >
                      <Rocket className="w-3 h-3" /> Activate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}
      {recs && recs.recommendations.length === 0 && recs.submissions_count > 0 && (
        <Section title="Best-fit recommendations" testId="consumer-recs-empty" className="mb-5">
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-3 text-[12px] text-slate-500">
            <Sparkles className="w-3.5 h-3.5 inline-block mr-1 text-slate-400" />
            {recs.submissions_count} screening submission{recs.submissions_count === 1 ? "" : "s"} on file, but no active
            program currently matches the {recs.screener_categories.join(", ") || "indicated"} category.{" "}
            <Link to="/portal/programs" className="text-blue-700 underline font-semibold">
              Build a matching program →
            </Link>
          </div>
        </Section>
      )}

      {/* Active vendor hand-offs (workflow_requests) for this consumer */}
      <Section title="Active programs · vendor hand-offs" testId="consumer-active-programs" className="mb-5">
        {workflowRows.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-3 text-[12px] text-slate-500">
            No vendor workflows running for this consumer yet.{" "}
            <Link to="/portal/workflows/new" className="text-blue-700 underline font-semibold">Activate a program →</Link>
          </div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Order ID</th>
                  <th className="text-left px-4 py-2.5">Vendor</th>
                  <th className="text-left px-4 py-2.5">Program</th>
                  <th className="text-left px-4 py-2.5">Workflow</th>
                  <th className="text-left px-4 py-2.5">Submitted</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {workflowRows.map((it) => (
                  <tr key={it.id} data-testid={`consumer-workflow-row-${it.id}`}>
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/portal/workflows/${it.id}`}
                        className="font-mono text-[11px] font-semibold text-blue-700 hover:text-blue-900"
                      >
                        {it.order_id}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">{it.vendor_name}</td>
                    <td className="px-4 py-2.5 capitalize text-slate-700">{it.program_type}</td>
                    <td className="px-4 py-2.5 text-slate-700 max-w-[24ch] truncate" title={it.requested_workflow}>
                      {it.requested_workflow}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">{fmtRelative(it.created_at)}</td>
                    <td className="px-4 py-2.5"><StatusPill status={it.status} /></td>
                    <td className="px-4 py-2.5 text-right">
                      <Link
                        to={`/portal/workflows/${it.id}`}
                        className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900"
                      >
                        Open <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50 text-[11px] text-slate-500 flex items-center justify-between">
              <span className="inline-flex items-center gap-1"><Workflow className="w-3 h-3" /> {workflowRows.length} workflow{workflowRows.length === 1 ? "" : "s"} for {consumer.name}</span>
              <Link to={`/portal/workflows?consumer=${encodeURIComponent((consumer.name || "").split(/\s+/)[0] || "")}`} className="text-blue-700 hover:underline font-semibold">
                See in Active Programs →
              </Link>
            </div>
          </div>
        )}
      </Section>

      {/* Active services */}
      <Section title="Active services" testId="active-services">
        {active.length === 0 ? (
          <div className="text-sm text-slate-500 py-4">No active services. Activate one to begin.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {active.map((s) => (
              <ServiceCard key={s.id} s={s} onStatus={setStatus} onRemove={remove} />
            ))}
          </div>
        )}
      </Section>

      {/* Past / paused / cancelled */}
      {past.length > 0 && (
        <Section title="History" testId="past-services" className="mt-5">
          <div className="grid md:grid-cols-2 gap-3">
            {past.map((s) => (
              <ServiceCard key={s.id} s={s} onStatus={setStatus} onRemove={remove} />
            ))}
          </div>
        </Section>
      )}
    </PageShell>
  );
}

function Section({ title, testId, className = "", children }) {
  return (
    <div className={className} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">{title}</div>
      {children}
    </div>
  );
}

function ServiceCard({ s, onStatus, onRemove }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`service-card-${s.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className={`inline-block rounded-full px-2 py-0.5 text-[10px] uppercase font-bold ${CATEGORY_TINT[s.program_category] || "bg-slate-100 text-slate-700"}`}>
            {s.program_category}
          </div>
          <div className="font-display text-base font-bold text-slate-900 mt-1">{s.program_name}</div>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase font-bold ${STATUS_BADGE[s.status]}`}>
          {s.status}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <Mini icon={DollarSign} label="Price" v={`$${s.price}`} />
        <Mini icon={Calendar} label="Cadence" v={s.cadence?.replace("_", " ")} />
        <Mini icon={RefreshCw} label="Cycles" v={s.cycles_completed || 0} />
      </div>
      <div className="text-[11px] text-slate-500 mt-2">
        Started {fmtDate(s.started_at)}{s.next_billing_at && ` · Next billing ${fmtDate(s.next_billing_at)}`}
        {s.cancelled_at && ` · Cancelled ${fmtDate(s.cancelled_at)}`}
      </div>
      {s.notes && <div className="text-[11px] text-slate-700 mt-1.5 italic">"{s.notes}"</div>}
      <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-3 border-t border-slate-100">
        {s.status === "active" && (
          <>
            <button
              onClick={() => onStatus(s.id, "paused")}
              data-testid={`service-pause-${s.id}`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-50 hover:bg-amber-100 rounded-full px-2.5 py-1"
            >
              <Pause className="w-3 h-3" /> Pause
            </button>
            <button
              onClick={() => onStatus(s.id, "cancelled")}
              data-testid={`service-cancel-${s.id}`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 bg-rose-50 hover:bg-rose-100 rounded-full px-2.5 py-1"
            >
              <XCircle className="w-3 h-3" /> Cancel
            </button>
          </>
        )}
        {(s.status === "paused" || s.status === "cancelled") && (
          <button
            onClick={() => onStatus(s.id, "active")}
            data-testid={`service-resume-${s.id}`}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 rounded-full px-2.5 py-1"
          >
            <Play className="w-3 h-3" /> {s.status === "cancelled" ? "Reactivate" : "Resume"}
          </button>
        )}
        {(s.cycles_completed || 0) === 0 && (
          <button
            onClick={() => onRemove(s.id)}
            data-testid={`service-remove-${s.id}`}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-rose-700 ml-auto"
            title="Remove service entirely (only allowed when no cycles billed)"
          >
            <XCircle className="w-3 h-3" /> Remove
          </button>
        )}
      </div>
    </div>
  );
}
function Mini({ icon: Icon, label, v }) {
  return (
    <div className="bg-slate-50 rounded p-1.5 flex items-center gap-1.5">
      <Icon className="w-3 h-3 text-slate-400" />
      <span className="text-[10px] text-slate-500">{label}</span>
      <span className="ml-auto font-bold text-slate-900 capitalize">{v}</span>
    </div>
  );
}

void Plus;
